import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { useParams } from 'react-router-dom';
import { Tabs, Tab, Form, Button, Alert, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import moment from 'moment-timezone';
import { fetchCustomerNodeDefinitionByZoneAndName } from '../../../../redux/slices/customer_node_definitionsSlice';
import { useTranslation } from 'react-i18next';
import { submitNode } from '../../../../redux/slices/nodeSubmitSlice';
import api from 'services/api';
import { getQueuesPaginated } from '../../../../redux/slices/lists/queuesSlice';
import { useAppSelector, useAppDispatch } from 'redux/store';
import { selectHour12Option } from 'redux/selectors/userSettings';
import { fetchSchedulingWindows } from '../../../../redux/slices/scheduling_windowsSlice';
import { fetchRecurrenceDefinitions } from '../../../../redux/slices/recurrenceDefinitionsSlice';
import { buildManualSnapshot } from './manualRecurrence';
import { DetailCard } from 'components/details/DetailCard';
import { SubmitScheduleFields } from './SubmitScheduleFields';
import { EditSchedulingWindowReference, type SchedulingWindowReference } from 'edit/reference/EditSchedulingWindowReference';
import { EditTimeZoneReference } from 'edit/reference/EditTimeZoneReference';
import { dateTimeZoneFallback, parseDateTimeZoneValue } from './dateTimeZoneParameter';
import OpeningPeriodsButton from './OpeningPeriodsButton';
import MaskedDateTimeField from 'components/forms/MaskedDateTimeField';
import { EditBoolean } from 'components/forms/EditBoolean';
import { SubmitScheduleModel } from './SubmitScheduleModel';
import SchedulePreviewPanel from './SchedulePreviewPanel';

// Neither the recurrence schedule nor the start datetime / timezone / window
// live in `submitBody`. All scheduling state belongs to `SubmitScheduleModel`;
// `handleSubmit` reads it from there and assembles whichever of `schedule` /
// `recurrence_definition_id` applies onto the request body.
interface SubmitParameter {
  name: string;
  value: unknown;
}

interface SubmitBody {
  name: string;
  queue_path: string;
  parameters: SubmitParameter[];
  hold_on_submit: boolean;
  note: string;
  start_node_id: string | null;
}

const INITIAL_SUBMIT_BODY: SubmitBody = {
  name: '',
  queue_path: '',
  parameters: [],
  hold_on_submit: false,
  note: '',
  start_node_id: null,
};

const normalizeKeyValuePairs = (val: unknown) => {
  if (typeof val !== 'string') {
    return val as any;
  }
  const i = val.indexOf('=');
  return (i >= 0 ? val.slice(0, i) : val).trim();
};

const getDropdownOptions = (optionsStr: string) =>
  (optionsStr || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const parseKeyValueOptions = (s: string) =>
  (s || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .map((pair) => {
      const [key, ...rest] = pair.split('=');
      const label = rest.join('=').trim();
      return { key: (key ?? '').trim(), label };
    })
    .filter(({ key, label }) => key && label);

const normalizeParamType = (t: any) =>
  String(t ?? '')
    .replace(/\s+/g, '')
    .toLowerCase();

const isOutputOnlyParameter = (param: any) => param?.inout === 'out';

const NodeSubmit: React.FC = observer(() => {
  const { zone_name, name, instance_id, node_definition_id } = useParams<{
    zone_name: string;
    name: string;
    instance_id: string;
    node_definition_id: string;
  }>();

  useEffect(() => {
    const prev = document.title;
    const base = instance_id ? `Submit Instance - ${instance_id} - ${name}` : `Submit Definition - ${node_definition_id} - ${name}`;

    document.title = base;
    return () => {
      document.title = prev;
    };
  }, [instance_id, node_definition_id]);

  const [selectedTab, setSelectedTab] = useState('submit');
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // TODO #757 step 3 PR D: extend CustomerNodeDefinition in the slice with
  // child_nodes, parameters, default_queue_path so this cast goes away.
  const nodeDefinition = useAppSelector((state) =>
    state.customerNodeDefinitions.nodes.find((node: any) => node.zone_name === zone_name && node.name === name)
  ) as any;
  const queues = useAppSelector((state) => state.listOfQueues?.queues || []);
  const rootNodeInstanceId = useAppSelector((state) => state.nodeSubmit.rootNodeInstanceId);

  const [submitBody, setSubmitBody] = useState(INITIAL_SUBMIT_BODY);
  const [showAlert, setShowAlert] = useState(false);
  const schedulingWindows = useAppSelector((state) => state.schedulingWindow.items);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDescriptions, setShowDescriptions] = useState(false);

  // All scheduling state — datetime, timezone, window, recurrence, eligible
  // periods, schedule preview — lives here. Stable for the component's lifetime.
  const schedule = useMemo(() => new SubmitScheduleModel(), []);
  // start() returns its own teardown, so StrictMode's mount/unmount/mount
  // cycle re-arms the model instead of leaving it permanently torn down.
  useEffect(() => schedule.start(), [schedule]);

  // Recurrence definitions
  const recurrenceDefinitions = useAppSelector((state) => state.recurrenceDefinitions.nodes);
  const hour12 = useAppSelector(selectHour12Option);
  // moment time token for user-facing displays; 12h preference → AM/PM,
  // otherwise 24h (also the 'auto' default for these moment-formatted strings).
  const displayTimeToken = hour12 ? 'h:mm A' : 'HH:mm';

  // The model must not read Redux itself, so hand it the store-derived values
  // it needs. Refreshed every render, which is what makes a mid-session 12h/24h
  // preference change format the next suggestion correctly.
  schedule.setContext({ schedulingWindows, recurrenceDefinitions, displayTimeToken });

  // Bridge between this dialog's wire-string state and the object-based
  // EditReference wrapper. Inflate to a partial T for display; the wrapper's
  // onChange handler below deflates back.
  const schedulingWindowValue: SchedulingWindowReference | null = useMemo(() => {
    if (!schedule.schedulingWindow) {
      return null;
    }
    const [zone_name, name] = schedule.schedulingWindow.split('|');
    return name !== undefined ? { zone_name, name } : null;
  }, [schedule.schedulingWindow]);
  const appliedHoldDefaultRef = useRef<string | null>(null);

  const [paramTimezones, setParamTimezones] = useState<Record<string, string>>({});

  const startNodeOptions = useMemo(() => {
    if (!Array.isArray(nodeDefinition?.child_nodes)) {
      return [];
    }
    return nodeDefinition.child_nodes.map((n: any) => ({
      label: `${n.node_definition_name || n.name || n.data?.label || 'Unknown'} (${n.type}) — ${n.id}`,
      value: n.id,
    }));
  }, [nodeDefinition]);

  const sortedParameters = useMemo(() => {
    if (!nodeDefinition?.parameters) {
      return [];
    }

    return [...nodeDefinition.parameters]
      .filter((param) => !isOutputOnlyParameter(param))
      .sort((a, b) => {
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
  }, [nodeDefinition?.parameters]);

  useEffect(() => {
    if (zone_name && name) {
      dispatch(
        fetchCustomerNodeDefinitionByZoneAndName({
          zone_name,
          node_definition_name: name,
        })
      );
    }
  }, [dispatch, zone_name, name]);

  const getNodeInstanceDetails = async () => {
    return api.get(`/node_instance_details/${instance_id}`);
  };

  const getNodeInstanceById = async () => {
    return api.get(`/node_instances/${instance_id}`);
  };

  useEffect(() => {
    if (instance_id) {
      const fetchData = async () => {
        try {
          const response = await getNodeInstanceDetails();
          const fullResponse = await getNodeInstanceById();

          if (response.data.node_instance.parameters) {
            setSubmitBody((prev) => ({
              ...prev,
              parameters: response.data.node_instance.parameters,
            }));
          }

          if (response.data.node_instance.queue_path) {
            setSubmitBody((prev) => ({
              ...prev,
              queue_path: response.data.node_instance.queue_path,
            }));
          }

          // A resubmit is a one-off rerun of this instance: it starts with no
          // recurrence so the operator can't accidentally register a second
          // scheduled chain (#952). The source instance's recurrence is left
          // untouched. The controls stay editable — the operator may still add
          // a recurrence deliberately, which submits as normal.
          schedule.seedFromInstance({
            schedulingWindow: fullResponse.data.scheduling_window,
            datetime: fullResponse.data.scheduled_start_datetime,
            timezone: fullResponse.data.scheduled_start_timezone,
          });

          if (fullResponse.data.start_node_id) {
            setSubmitBody((prev) => ({
              ...prev,
              start_node_id: fullResponse.data.start_node_id,
            }));
          }

          if (fullResponse.data.hold_on_submit !== undefined) {
            setSubmitBody((prev) => ({
              ...prev,
              hold_on_submit: !!fullResponse.data.hold_on_submit,
            }));
          }
        } catch (error) {
          console.error('Error fetching node instance details:', error);
        }
      };
      fetchData();
    }
  }, [instance_id]);

  useEffect(() => {
    dispatch(getQueuesPaginated({ page: 1, limit: 2000 }));
    dispatch(fetchSchedulingWindows({}));
    dispatch(fetchRecurrenceDefinitions({}));
  }, [dispatch]);

  useEffect(() => {
    if (!nodeDefinition || instance_id) {
      return;
    }
    const holdDefault = (nodeDefinition as any).hold_on_submit;
    if (holdDefault === undefined) {
      return;
    }

    // Apply definition default once per definition; don't overwrite later user toggles.
    const definitionKey = String(nodeDefinition.node_definition_id ?? '');
    if (appliedHoldDefaultRef.current === definitionKey) {
      return;
    }
    const holdOnSubmitDefault = Boolean(holdDefault);

    setSubmitBody((prev) => ({
      ...prev,
      hold_on_submit: holdOnSubmitDefault,
    }));
    appliedHoldDefaultRef.current = definitionKey;
  }, [nodeDefinition, instance_id]);

  const handleTabChange = (key: string | null) => {
    if (key) {
      setSelectedTab(key);
    }
  };

  const handleQueueChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> = (e) => {
    const queue_path = e.currentTarget.value;
    setSubmitBody((prev) => ({ ...prev, queue_path }));
  };

  const handleParameterChange = (paramName: string, value: string) => {
    setSubmitBody((prev) => {
      const newParameters = [...prev.parameters];
      const def = nodeDefinition.parameters.find((p: any) => p.name === paramName);

      if (!def) {
        return prev;
      }

      const normalizedType = String(def.parameter_type || '')
        .replace(/\s+/g, '')
        .toLowerCase();
      const finalValue = normalizedType === 'keyvaluedropdown' ? normalizeKeyValuePairs(value) : value;

      const existingParamIndex = newParameters.findIndex((param) => param.name === paramName);

      if (existingParamIndex !== -1) {
        newParameters[existingParamIndex].value = finalValue;
      } else {
        newParameters.push({ name: paramName, value: finalValue });
      }

      return { ...prev, parameters: newParameters };
    });
  };

  const appliedDateDefaultsRef = useRef(false);
  useEffect(() => {
    if (instance_id) {
      return; // don't override instance-loaded values
    }
    if (!nodeDefinition?.parameters?.length) {
      return;
    }
    if (appliedDateDefaultsRef.current) {
      return;
    }

    const defs = nodeDefinition.parameters as any[];

    const defaultsToApply = defs
      .map((p, idx) => ({ p, idx }))
      .filter(({ p }) => {
        const t = normalizeParamType(p?.parameter_type);
        const isDate = t === 'date';
        const isDateTime = t === 'datetime';
        const isDateTimeZone = t === 'datetimezone';
        return (isDate || isDateTime || isDateTimeZone) && !!String(p?.default_expression ?? '').trim();
      });

    if (defaultsToApply.length === 0) {
      appliedDateDefaultsRef.current = true;
      return;
    }

    setSubmitBody((prev) => {
      const nextParams = [...(prev.parameters || [])];

      defaultsToApply.forEach(({ p, idx }) => {
        const name = p.name;
        const existingIdx = nextParams.findIndex((x: any) => x.name === name);
        if (existingIdx !== -1) {
          return; // already set
        }
        nextParams.push({ name, value: String(p.default_expression ?? '') });
      });

      return { ...prev, parameters: nextParams };
    });

    appliedDateDefaultsRef.current = true;
  }, [nodeDefinition, instance_id]);

  const handleDateParamChange = (paramName: string, datetime: moment.Moment | string) => {
    if (typeof datetime === 'string') {
      if (!datetime.trim()) {
        handleParameterChange(paramName, '');
      }
      return;
    }
    const sanitized = datetime.clone().seconds(0).milliseconds(0);
    handleParameterChange(paramName, sanitized.format('YYYY-MM-DD'));
  };

  const handleDateTimeParamChange = (paramName: string, datetime: moment.Moment | string) => {
    if (typeof datetime === 'string') {
      if (!datetime.trim()) {
        handleParameterChange(paramName, '');
      }
      return;
    }
    const sanitized = datetime.clone().seconds(0).milliseconds(0);
    handleParameterChange(paramName, sanitized.format('YYYY-MM-DDTHH:mm:ss'));
  };

  const handleDateTimeZoneParamChange = (paramName: string, datetime: moment.Moment | string) => {
    const def = nodeDefinition.parameters.find((p: any) => p.name === paramName);
    if (!def) {
      return;
    }

    if (typeof datetime === 'string') {
      if (!datetime.trim()) {
        handleParameterChange(paramName, '');
      }
      return;
    }

    const tz = paramTimezones[paramName] ?? dateTimeZoneFallback(getShownParamValue(paramName)).zone;
    const sanitized = datetime.clone().seconds(0).milliseconds(0);

    handleParameterChange(paramName, `${sanitized.format('YYYY-MM-DDTHH:mm:ss')} ${tz}`);
  };

  const handleTimeParamChange = (paramName: string, datetime: moment.Moment | string) => {
    if (typeof datetime === 'string') {
      if (!datetime.trim()) {
        handleParameterChange(paramName, '');
      }
      return;
    }
    handleParameterChange(paramName, datetime.format('HH:mm:ss'));
  };

  const handleDateTimeZoneParamTimezoneChange = (paramName: string, timezone: string | null) => {
    // An emptied picker leaves the value as it is: the zone it already carries is the fallback shown.
    if (timezone === null) {
      setParamTimezones(({ [paramName]: _cleared, ...rest }) => rest);
      return;
    }

    setParamTimezones((prev) => ({ ...prev, [paramName]: timezone }));

    const parsed = parseDateTimeZoneValue(getShownParamValue(paramName));

    if (!parsed.datetime) {
      return;
    }

    handleParameterChange(paramName, `${parsed.datetime} ${timezone}`);
  };

  const handleStartNodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSubmitBody((prev) => ({
      ...prev,
      start_node_id: val === 'full' ? null : val,
    }));
  };

  // On submit, convert the stored "naive" time back into
  // an ISO string with the correct offset for scheduled_start_timezone.
  const handleSubmit = async () => {
    // Never true: every route that mounts NodeSubmit requires both params, and without
    // them no definition loads, so the form (and this button) never renders. The
    // check is what narrows them to the `string` submitNode takes.
    if (!zone_name || !name) {
      return;
    }
    const nodeParamDefs = (nodeDefinition?.parameters || []).filter((paramDefinition: any) => !isOutputOnlyParameter(paramDefinition));

    // Build params from definitions, falling back to default_expression when the user didn't touch a field.
    const normalizedParams = (nodeParamDefs as any[])
      .map((def) => {
        const existing = (submitBody.parameters || []).find((p: any) => p.name === def.name);

        const hasExisting = existing && existing.value !== undefined && existing.value !== null && String(existing.value).trim() !== '';

        const defDefault = String(def?.default_expression ?? '').trim();
        const hasDefault = !!defDefault;

        // Keep prior behavior: don't send totally empty params
        if (!hasExisting && !hasDefault) {
          return null;
        }

        const normalizedType = normalizeParamType(def?.parameter_type);

        let value = hasExisting ? existing.value : defDefault;

        if (normalizedType === 'keyvaluedropdown') {
          value = normalizeKeyValuePairs(value);
        }

        return { name: def.name, value };
      })
      .filter(Boolean) as any[];

    setIsSubmitting(true);

    const timezone = schedule.effectiveTimezone;

    // Under `asap` the start is deliberately NOT sent: the schedule decides it,
    // and only the backend can decide it correctly — against its own clock, and
    // through the engine that will actually schedule the occurrences. Anything
    // resolved here would be a value read before the operator pressed the button.
    // Under `requested` the operator's own value is sent RAW; the backend applies
    // the scheduling window, so this does not pre-empt that either.
    let requestedStart: string | null = null;
    if (schedule.intent === 'requested' && schedule.requestedDatetime) {
      // Strip any trailing Z or offset and reinterpret as wall clock in `timezone`,
      // then keep that offset in the string so the instant is unambiguous.
      const base = schedule.requestedDatetime.replace(/([Zz]|[+-]\d{2}:\d{2})$/, '');
      requestedStart = moment.tz(base, timezone).toISOString(true);
    }

    const bodyToSubmit: any = {
      ...submitBody,
      scheduled_start_datetime: requestedStart,
      scheduled_start_timezone: timezone,
      scheduling_window: schedule.schedulingWindow,
      parameters: normalizedParams,
    };

    const trimmedNote = String(submitBody.note || '').trim();
    if (trimmedNote) {
      bodyToSubmit.note = trimmedNote;
    } else {
      delete bodyToSubmit.note;
    }

    // Schedule XOR recurrence_definition_id, decided by the chosen schedule
    // source: a picked definition is submitted live by id; the ad-hoc rule is
    // frozen into a schedule snapshot. Neither is sent for a one-off — including
    // when the chosen source has nothing filled in yet.
    delete bodyToSubmit.schedule;
    delete bodyToSubmit.recurrence_definition_id;
    if (schedule.usesManualRule) {
      const snapshot = buildManualSnapshot(schedule.manualDefinition);
      if (snapshot) {
        bodyToSubmit.schedule = snapshot;
      }
    } else if (schedule.scheduleSource === 'definition' && schedule.selectedRecurrenceId) {
      bodyToSubmit.recurrence_definition_id = Number(schedule.selectedRecurrenceId);
    }

    if (nodeDefinition?.version != null) {
      (bodyToSubmit as any).if_match_version = nodeDefinition.version;
    }

    if (instance_id) {
      bodyToSubmit.source_instance_id = Number(instance_id);
    }

    try {
      const resultAction = await dispatch(
        submitNode({
          zone_name,
          name,
          data: bodyToSubmit,
        })
      );

      if (submitNode.fulfilled.match(resultAction)) {
        setShowAlert(true);
        setErrorMessage('');
        setSubmitBody((prev) => ({
          ...prev,
          note: '',
        }));
      } else if (submitNode.rejected.match(resultAction)) {
        const payload = resultAction.payload as { error: string } | undefined;
        const errorMessage = payload?.error || resultAction.error?.message || 'Submission failed.';
        setErrorMessage(errorMessage);
      }
    } catch (error) {
      console.error('Error submitting node:', error);
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
      }, 3000);
    }
  };

  if (!nodeDefinition) {
    return <div>Loading...</div>;
  }

  const getParamValue = (paramName: string) => submitBody.parameters.find((p: any) => p.name === paramName)?.value ?? '';

  // What a parameter's field shows, and so what its handlers must edit: its value, else the definition's default. A resubmit
  // does not seed defaults into `submitBody`, so there the bare value can be empty while the field shows the default.
  const getShownParamValue = (paramName: string) => {
    const value = getParamValue(paramName);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    return nodeDefinition?.parameters?.find((p: any) => p.name === paramName)?.default_expression ?? '';
  };

  const isQueueSelectionMissing = !nodeDefinition.default_queue_path && submitBody.queue_path === '';
  // Nothing the schedule preview reports gates this button. The preview is a
  // courtesy, fetched asynchronously and possibly seconds behind; the backend
  // performs the same checks on submit and is the only authority on them, so
  // blocking here would only ever be a slower way to reach the same answer —
  // and an unschedulable submit is refused with a 400 either way, which the
  // panel already explains. What remains are conditions this form owns.
  const submitDisabledReason = (() => {
    if (isSubmitting) {
      return 'Submission in progress.';
    }
    if (isQueueSelectionMissing) {
      return 'Select a queue before submitting.';
    }
    return null;
  })();
  const isSubmitDisabled = Boolean(submitDisabledReason);

  const renderSubmitButton = (tooltipId: string) => {
    if (submitDisabledReason) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={tooltipId} style={{ position: 'fixed' }}>
              {submitDisabledReason}
            </Tooltip>
          }
        >
          <span className="d-inline-block">
            <Button disabled style={{ pointerEvents: 'none' }}>
              Submit
            </Button>
          </span>
        </OverlayTrigger>
      );
    }

    return (
      <Button disabled={isSubmitDisabled} onClick={handleSubmit}>
        Submit
      </Button>
    );
  };

  const renderInitialNoteField = (controlId: string) => (
    <Form.Group controlId={controlId} className="mb-3">
      <Form.Label>Initial Note</Form.Label>
      <Form.Control
        as="textarea"
        rows={3}
        placeholder="Optional note to attach to the created node instance"
        value={submitBody.note}
        onChange={(e) =>
          setSubmitBody((prev) => ({
            ...prev,
            note: e.target.value,
          }))
        }
      />
      <Form.Text className="text-muted">This note will be attached to the created node instance.</Form.Text>
    </Form.Group>
  );

  return (
    <div
      className="p-4"
      style={{
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      {showAlert && errorMessage === '' && (
        <Alert variant="outline-success" onClose={() => setShowAlert(false)} dismissible>
          Successfully submitted{' '}
          <a href={`/automation/node_instances?node_instance_id=${rootNodeInstanceId}`} target="_parent">
            View Node Instance
          </a>
        </Alert>
      )}
      {errorMessage && (
        <Alert variant="outline-danger" onClose={() => setShowAlert(false)} dismissible>
          {errorMessage}
        </Alert>
      )}
      <h3>Submit Node Definition: {name}</h3>
      <Tabs id="node-submit-tabs" activeKey={selectedTab} onSelect={handleTabChange} className="mb-2 border-bottom" variant="underline">
        <Tab eventKey="submit" title="Submit">
          <div>
            {/* "As soon as the schedule allows" is worded to hold in all four
                combinations of recurrence and window. It is deliberately NOT "as
                soon as possible": an anchored recurrence waits for its next slot,
                and an off-grid first start is legal, so that slot is later than
                the earliest permissible moment. */}
            <div className="mb-3">
              <DetailCard title="Scheduled start">
                {/* Row/Col rather than hand-rolled flex: the columns must sit side
                  by side down to the sm breakpoint instead of wrapping the panel
                  underneath as soon as the form gets a little narrow. */}
                <Row>
                  <Col sm={6}>
                    {/* "As soon as the schedule allows" is worded to hold in all four
                      combinations of recurrence and window. It is deliberately NOT "as
                      soon as possible": an anchored recurrence waits for its next slot,
                      and an off-grid first start is legal, so that slot is later than
                      the earliest permissible moment. */}
                    <Form.Check
                      type="radio"
                      id="scheduled-start-asap"
                      name="scheduled_start_intent"
                      label="As soon as the schedule allows"
                      checked={schedule.intent === 'asap'}
                      onChange={() => schedule.handleIntentChange('asap')}
                    />
                    <Form.Check
                      type="radio"
                      id="scheduled-start-requested"
                      name="scheduled_start_intent"
                      label="Requested start DateTime"
                      checked={schedule.intent === 'requested'}
                      onChange={() => schedule.handleIntentChange('requested')}
                    />
                    {/* Kept mounted but disabled under `asap`, so the control does
                      not appear and disappear as the radio is toggled. */}
                    <OverlayTrigger
                      placement="top"
                      overlay={schedule.datetimeTooltip ? <Tooltip id="scheduled-start-datetime-tooltip">{schedule.datetimeTooltip}</Tooltip> : <></>}
                    >
                      <div className="mt-2 ms-4">
                        <MaskedDateTimeField
                          key={schedule.schedulingWindow}
                          value={schedule.pickerValue}
                          onChange={(value) => {
                            if (typeof value !== 'string') {
                              schedule.handleDatetimeChange(value);
                            }
                          }}
                          mode="datetime"
                          placeholder="Type or select date and time"
                          disabled={schedule.intent !== 'requested'}
                          isValidDate={schedule.schedulingWindow && schedule.availablePeriods.length > 0 ? schedule.isDateValid : undefined}
                        />
                      </div>
                    </OverlayTrigger>

                    {/* The zone the datetime above is read in, so it belongs
                        beside it rather than in a group of its own. */}
                    <Form.Group controlId="scheduled_start_timezone" className="mt-3">
                      <Form.Label>Scheduled Start Timezone</Form.Label>
                      <EditTimeZoneReference
                        value={schedule.timezone}
                        onChange={schedule.handleTimezoneChange}
                        placeholder={`${schedule.effectiveTimezone} (local)`}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <SchedulePreviewPanel schedule={schedule} displayTimeToken={displayTimeToken} />
                  </Col>
                </Row>
              </DetailCard>
            </div>

            <div className="mb-3">
              <DetailCard title="Execution">
                <Form.Group controlId="queue_name" className="mb-3">
                  <Form.Label>Queue</Form.Label>
                  <Form.Control as="select" value={submitBody.queue_path ?? ''} onChange={handleQueueChange}>
                    {instance_id ? (
                      <option value={submitBody.queue_path}>{submitBody.queue_path}</option>
                    ) : (
                      <option value="">Default ({nodeDefinition.default_queue_path})</option>
                    )}
                    {queues.map((queue: any) => (
                      <option key={queue.queue_id} value={queue.zone_name + '|' + queue.queue_name}>
                        {queue.queue_name} ({queue.zone_name})
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>

                <Form.Group controlId="schedulingWindow" className="mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <div className="flex-grow-1">
                      <EditSchedulingWindowReference
                        label="Scheduling Window"
                        value={schedulingWindowValue}
                        onChange={(picked) => schedule.handleSchedulingWindowChange(picked ? `${picked.zone_name}|${picked.name}` : null)}
                        disabled={Number.isInteger(nodeDefinition?.restrict_to_scheduling_window)}
                      />
                    </div>
                    <OpeningPeriodsButton
                      periods={[...schedule.availablePeriods]}
                      windowName={schedule.schedulingWindow}
                      disabled={!schedule.schedulingWindow || schedule.isWindowLoading || schedule.availablePeriods.length === 0}
                      timezone={schedule.effectiveTimezone}
                    />
                  </div>
                  {schedule.isWindowLoading && <div className="text-muted small mt-1">Checking scheduling window...</div>}
                </Form.Group>

                <EditBoolean
                  label="Hold on submit"
                  value={!!submitBody?.hold_on_submit}
                  onChange={(v) => setSubmitBody((prev) => ({ ...prev, hold_on_submit: v }))}
                />
              </DetailCard>
            </div>

            <div className="mb-3">
              <DetailCard title="Recurrence">
                <SubmitScheduleFields schedule={schedule} />
              </DetailCard>
            </div>

            {Array.isArray(nodeDefinition.child_nodes) && nodeDefinition.child_nodes.length > 0 && (
              <Form.Group className="mb-3" controlId="start_node_id">
                <Form.Label className="mb-1">Start from</Form.Label>
                <Form.Select value={submitBody.start_node_id ?? 'full'} onChange={handleStartNodeChange}>
                  <option value="full">Run entire flow</option>
                  {startNodeOptions.map((n: any) => (
                    <option key={n.value} value={n.value}>
                      {n.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {renderInitialNoteField('submit_note_main')}
            {renderSubmitButton('submit-disabled-queue-tooltip-main')}
          </div>
        </Tab>

        <Tab eventKey="parameters" title="Parameters">
          <div>
            <Form.Check
              type="switch"
              id="show-descriptions-switch-submit"
              label="Show descriptions"
              checked={showDescriptions}
              onChange={(e) => setShowDescriptions(e.target.checked)}
              className="mb-3"
            />
            {sortedParameters.map((param: any) => {
              const normalizedType = normalizeParamType(param.parameter_type);
              const isDropdown = normalizedType === 'dropdown';
              const isKeyValueDropdown = normalizedType === 'keyvaluedropdown';
              const isDate = normalizedType === 'date';
              const isDateTime = normalizedType === 'datetime';
              const isDateTimeZone = normalizedType === 'datetimezone';
              const isTime = normalizedType === 'time';

              const dropdownOptions = isDropdown ? getDropdownOptions(param.options || '') : [];

              const kvOptions = isKeyValueDropdown ? parseKeyValueOptions(param.options || '') : [];

              const rawCurrent = getShownParamValue(param.name);

              const currentValue = isKeyValueDropdown ? normalizeKeyValuePairs(String(rawCurrent)) : rawCurrent;

              const displayLabel = showDescriptions && param.description ? param.description : param.name;

              const dateMoment = (() => {
                const s = String(currentValue ?? '').trim();
                if (!s) {
                  return undefined;
                }
                const m = moment(s, 'YYYY-MM-DD', true);
                return m.isValid() ? m : undefined;
              })();

              const dateTimeMoment = (() => {
                const s = String(currentValue ?? '').trim();
                if (!s) {
                  return undefined;
                }
                const m = moment(s, moment.ISO_8601, true);
                return m.isValid() ? m : undefined;
              })();

              const dateTimeZoneParts = (() => {
                const parsed = parseDateTimeZoneValue(currentValue);
                const fallback = dateTimeZoneFallback(currentValue);
                const tzPick = paramTimezones[param.name] ?? null;
                const tzPlaceholder = `${fallback.zone} (${fallback.source})`;
                if (!parsed.datetime) {
                  return { momentValue: undefined, tzPick, tzPlaceholder };
                }
                const m = moment(parsed.datetime, moment.ISO_8601, true);
                return {
                  momentValue: m.isValid() ? m : undefined,
                  tzPick,
                  tzPlaceholder,
                };
              })();

              const timeMoment = (() => {
                const s = String(currentValue ?? '').trim();
                if (!s) {
                  return undefined;
                }
                const m = moment(s, 'HH:mm:ss', true);
                return m.isValid() ? m : undefined;
              })();

              const showClear = (isDate || isDateTime || isDateTimeZone || isTime) && String(currentValue ?? '').trim().length > 0;

              return (
                <Form.Group key={param.name} controlId={`param-${param.name}`} className="mb-3">
                  <Form.Label>{displayLabel}</Form.Label>

                  {isDropdown && (
                    <Form.Select
                      value={dropdownOptions.includes(String(currentValue)) ? String(currentValue) : ''}
                      onChange={(e) => handleParameterChange(param.name, e.target.value)}
                    >
                      {dropdownOptions.length === 0 ? (
                        <option value="">No options configured</option>
                      ) : (
                        <>
                          <option value=""></option>
                          {dropdownOptions.map((opt: string) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </>
                      )}
                    </Form.Select>
                  )}

                  {isKeyValueDropdown && (
                    <Form.Select
                      value={kvOptions.some((o) => o.key === String(currentValue)) ? String(currentValue) : ''}
                      onChange={(e) => handleParameterChange(param.name, e.target.value)}
                    >
                      {kvOptions.length === 0 ? (
                        <option value="">No options configured</option>
                      ) : (
                        kvOptions.map(({ key, label }) => (
                          <option key={key} value={key}>
                            {label} ({key})
                          </option>
                        ))
                      )}
                    </Form.Select>
                  )}

                  {isDate && (
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ flex: 1 }}>
                        <MaskedDateTimeField
                          value={dateMoment ?? ''}
                          onChange={(m) => handleDateParamChange(param.name, m)}
                          mode="date"
                          placeholder="YYYY-MM-DD"
                        />
                      </div>
                      <Button variant="outline-secondary" size="sm" disabled={!showClear} onClick={() => handleParameterChange(param.name, '')}>
                        Clear
                      </Button>
                    </div>
                  )}

                  {isDateTime && (
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ flex: 1 }}>
                        <MaskedDateTimeField
                          value={dateTimeMoment ?? ''}
                          onChange={(m) => handleDateTimeParamChange(param.name, m)}
                          mode="datetime"
                          placeholder="YYYY-MM-DD HH:mm:ss"
                        />
                      </div>
                      <Button variant="outline-secondary" size="sm" disabled={!showClear} onClick={() => handleParameterChange(param.name, '')}>
                        Clear
                      </Button>
                    </div>
                  )}

                  {isDateTimeZone && (
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <div style={{ flex: 1 }}>
                          <MaskedDateTimeField
                            value={dateTimeZoneParts.momentValue ?? ''}
                            onChange={(m) => handleDateTimeZoneParamChange(param.name, m)}
                            mode="datetime"
                            placeholder="YYYY-MM-DD HH:mm:ss"
                          />
                        </div>
                        <Button variant="outline-secondary" size="sm" disabled={!showClear} onClick={() => handleParameterChange(param.name, '')}>
                          Clear
                        </Button>
                      </div>
                      <EditTimeZoneReference
                        value={dateTimeZoneParts.tzPick}
                        onChange={(tz) => handleDateTimeZoneParamTimezoneChange(param.name, tz)}
                        placeholder={dateTimeZoneParts.tzPlaceholder}
                      />
                    </div>
                  )}

                  {isTime && (
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ flex: 1 }}>
                        <MaskedDateTimeField
                          value={timeMoment ?? ''}
                          onChange={(m) => handleTimeParamChange(param.name, m)}
                          mode="time"
                          showCalendar={false}
                          placeholder="HH:mm:ss"
                        />
                      </div>
                      <Button variant="outline-secondary" size="sm" disabled={!showClear} onClick={() => handleParameterChange(param.name, '')}>
                        Clear
                      </Button>
                    </div>
                  )}

                  {!isDropdown && !isKeyValueDropdown && !isDate && !isDateTime && !isDateTimeZone && !isTime && (
                    <Form.Control
                      type="text"
                      placeholder={param.default_expression}
                      value={String(currentValue)}
                      onChange={(e) => handleParameterChange(param.name, e.target.value)}
                    />
                  )}
                </Form.Group>
              );
            })}

            {renderInitialNoteField('submit_note_parameters')}
            {renderSubmitButton('submit-disabled-queue-tooltip-parameters')}
          </div>
        </Tab>
      </Tabs>
    </div>
  );
});

export default NodeSubmit;
